import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, Key, Copy, Check, Plus, Trash2, 
  Code, Book, Zap, Users, Calendar, FileText,
  Globe, Lock, ExternalLink, AlertCircle, Eye, EyeOff,
  ChevronRight, Terminal, Facebook, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Code block component with copy functionality
const CodeBlock = ({ code, language = 'bash' }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <div className="relative group">
      <pre className="bg-zinc-900 text-zinc-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-zinc-700 hover:bg-zinc-600 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} className="text-zinc-400" />}
      </button>
    </div>
  );
};

// API Key card component
const ApiKeyCard = ({ apiKey, onRevoke }) => {
  const [showKey, setShowKey] = useState(false);
  
  return (
    <div className="bg-white rounded-xl border border-zinc-200 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Key size={18} className="text-emerald-600" />
          </div>
          <div>
            <h4 className="font-medium text-zinc-900">{apiKey.name}</h4>
            <p className="text-sm text-zinc-500">
              {apiKey.key_prefix}••••••••••••
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            apiKey.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
          }`}>
            {apiKey.is_active ? 'Active' : 'Revoked'}
          </span>
          {apiKey.is_active && (
            <button
              onClick={() => onRevoke(apiKey.id)}
              className="p-2 hover:bg-red-50 rounded-lg text-red-500 transition-colors"
              title="Revoke key"
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
        <span>Scopes: {apiKey.scopes?.join(', ') || 'read'}</span>
        <span>Created: {new Date(apiKey.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
};

// Create API Key Modal
const CreateKeyModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(['read']);
  const [loading, setLoading] = useState(false);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const availableScopes = [
    { id: 'read', label: 'Read', description: 'Read-only access to all resources' },
    { id: 'write', label: 'Write', description: 'Create and update resources' },
    { id: 'delete', label: 'Delete', description: 'Delete resources' },
  ];
  
  const toggleScope = (scope) => {
    if (scopes.includes(scope)) {
      setScopes(scopes.filter(s => s !== scope));
    } else {
      setScopes([...scopes, scope]);
    }
  };
  
  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, scopes })
      });
      
      if (res.ok) {
        const data = await res.json();
        setNewKey(data);
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to create API key');
      }
    } catch (err) {
      console.error('Error creating API key:', err);
      alert('Failed to create API key');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyKey = () => {
    navigator.clipboard.writeText(newKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  const handleClose = () => {
    if (newKey) {
      onCreate();
    }
    onClose();
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
        {!newKey ? (
          <>
            <div className="p-6 border-b border-zinc-100">
              <h2 className="text-xl font-bold text-zinc-900">Create API Key</h2>
              <p className="text-sm text-zinc-500 mt-1">Generate a new key to access the API</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Key Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Production Key, Facebook Integration"
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Permissions</label>
                <div className="space-y-2">
                  {availableScopes.map(scope => (
                    <button
                      key={scope.id}
                      onClick={() => toggleScope(scope.id)}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        scopes.includes(scope.id)
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-zinc-200 hover:border-zinc-300'
                      }`}
                    >
                      <div className="text-left">
                        <p className="font-medium text-zinc-900">{scope.label}</p>
                        <p className="text-xs text-zinc-500">{scope.description}</p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        scopes.includes(scope.id)
                          ? 'border-emerald-500 bg-emerald-500'
                          : 'border-zinc-300'
                      }`}>
                        {scopes.includes(scope.id) && <Check size={12} className="text-white" />}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-zinc-50 flex gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!name.trim() || scopes.length === 0 || loading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? 'Creating...' : 'Create Key'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="p-6 border-b border-zinc-100">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Key size={24} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">API Key Created!</h2>
              <p className="text-sm text-zinc-500 mt-1 text-center">Copy your key now - it won't be shown again</p>
            </div>
            
            <div className="p-6">
              <div className="bg-zinc-900 rounded-lg p-4 font-mono text-sm text-emerald-400 break-all">
                {newKey.key}
              </div>
              
              <button
                onClick={handleCopyKey}
                className="w-full mt-3 flex items-center justify-center gap-2 py-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg font-medium transition-colors"
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </button>
              
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700">
                    Store this key securely. For security reasons, we can't show it again.
                    If you lose it, you'll need to create a new key.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 bg-zinc-50">
              <Button onClick={handleClose} className="w-full bg-emerald-600 hover:bg-emerald-700">
                Done
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Sidebar navigation
const DocsSidebar = ({ activeSection, onNavigate }) => {
  const sections = [
    { id: 'overview', label: 'Overview', icon: Book },
    { id: 'authentication', label: 'Authentication', icon: Lock },
    { id: 'api-keys', label: 'API Keys', icon: Key },
    { id: 'customers', label: 'Customers', icon: Users },
    { id: 'jobs', label: 'Jobs', icon: Calendar },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'webhooks', label: 'Webhooks', icon: Zap },
    { id: 'facebook-leads', label: 'Facebook Leads', icon: Facebook },
    { id: 'bulk-import', label: 'Bulk Import', icon: Upload },
  ];
  
  return (
    <nav className="space-y-1">
      {sections.map(section => {
        const Icon = section.icon;
        return (
          <button
            key={section.id}
            onClick={() => onNavigate(section.id)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
              activeSection === section.id
                ? 'bg-emerald-50 text-emerald-700 font-medium'
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            <Icon size={16} />
            {section.label}
          </button>
        );
      })}
    </nav>
  );
};

const ApiDocs = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [apiKeys, setApiKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userTier, setUserTier] = useState(null);
  
  useEffect(() => {
    loadApiKeys();
    loadUserTier();
  }, []);
  
  const loadApiKeys = async () => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/api-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApiKeys(data);
      }
    } catch (err) {
      console.error('Error loading API keys:', err);
    }
  };
  
  const loadUserTier = async () => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/subscription/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserTier(data.tier);
      }
    } catch (err) {
      console.error('Error loading user tier:', err);
    }
  };
  
  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }
    
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        loadApiKeys();
      }
    } catch (err) {
      console.error('Error revoking API key:', err);
    }
  };
  
  const hasApiAccess = userTier === 'professional' || userTier === 'enterprise';
  const baseUrl = API_URL?.replace('https://', '').replace('http://', '') || 'api.perfectpanes.com';
  
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold text-zinc-900">StreakFree API</h1>
              <p className="text-lg text-zinc-600 mt-2">
                Build powerful integrations with our RESTful API
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
                <Users size={24} className="mb-3" />
                <h3 className="font-semibold text-lg">Customers</h3>
                <p className="text-emerald-100 text-sm mt-1">Create, read, update, and delete customers</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                <Calendar size={24} className="mb-3" />
                <h3 className="font-semibold text-lg">Jobs</h3>
                <p className="text-blue-100 text-sm mt-1">Manage scheduled jobs and appointments</p>
              </div>
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                <Zap size={24} className="mb-3" />
                <h3 className="font-semibold text-lg">Webhooks</h3>
                <p className="text-purple-100 text-sm mt-1">Receive real-time events via webhooks</p>
              </div>
            </div>
            
            <div className="bg-zinc-50 rounded-xl p-6">
              <h3 className="font-semibold text-zinc-900 mb-4">Quick Start</h3>
              <CodeBlock
                language="bash"
                code={`# List your customers
curl -X GET "https://${baseUrl}/api/v1/customers" \\
  -H "Authorization: Bearer pp_live_your_api_key"`}
              />
            </div>
            
            <div className="border border-zinc-200 rounded-xl p-6">
              <h3 className="font-semibold text-zinc-900 mb-2">Base URL</h3>
              <code className="bg-zinc-100 px-3 py-1 rounded text-sm text-zinc-800">
                https://{baseUrl}/api/v1
              </code>
            </div>
          </div>
        );
        
      case 'authentication':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Authentication</h2>
              <p className="text-zinc-600 mt-2">
                All API requests require authentication using an API key.
              </p>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Lock size={20} className="text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-emerald-900">API Key Required</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    API access is available on Professional and Enterprise plans.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-zinc-900 mb-3">Using your API Key</h3>
              <p className="text-zinc-600 mb-4">
                Include your API key in the Authorization header:
              </p>
              <CodeBlock
                code={`Authorization: Bearer pp_live_xxxxxxxxxxxxxxxxxxxx`}
              />
              
              <p className="text-zinc-600 mt-4 mb-4">
                Or use the X-API-Key header:
              </p>
              <CodeBlock
                code={`X-API-Key: pp_live_xxxxxxxxxxxxxxxxxxxx`}
              />
            </div>
            
            <div>
              <h3 className="font-semibold text-zinc-900 mb-3">Example Request</h3>
              <CodeBlock
                code={`curl -X GET "https://${baseUrl}/api/v1/customers" \\
  -H "Authorization: Bearer pp_live_your_api_key" \\
  -H "Content-Type: application/json"`}
              />
            </div>
            
            <div className="border border-zinc-200 rounded-xl p-4">
              <h4 className="font-medium text-zinc-900 mb-2">Rate Limits</h4>
              <ul className="text-sm text-zinc-600 space-y-1">
                <li>• 60 requests per minute</li>
                <li>• 10,000 requests per day</li>
              </ul>
            </div>
          </div>
        );
        
      case 'api-keys':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-zinc-900">API Keys</h2>
                <p className="text-zinc-600 mt-1">Manage your API keys</p>
              </div>
              {hasApiAccess && (
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  <Plus size={16} className="mr-2" />
                  Create Key
                </Button>
              )}
            </div>
            
            {!hasApiAccess ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                <Lock size={32} className="mx-auto text-amber-500 mb-3" />
                <h3 className="font-semibold text-amber-900">Upgrade Required</h3>
                <p className="text-amber-700 text-sm mt-2">
                  API access requires a Professional or Enterprise subscription.
                </p>
                <Button className="mt-4 bg-amber-600 hover:bg-amber-700">
                  View Plans
                </Button>
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="bg-zinc-50 rounded-xl p-8 text-center">
                <Key size={32} className="mx-auto text-zinc-400 mb-3" />
                <h3 className="font-semibold text-zinc-900">No API Keys</h3>
                <p className="text-zinc-500 text-sm mt-2">
                  Create your first API key to start using the API.
                </p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  Create Your First Key
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map(key => (
                  <ApiKeyCard key={key.id} apiKey={key} onRevoke={handleRevokeKey} />
                ))}
              </div>
            )}
            
            <div className="bg-zinc-50 rounded-xl p-4">
              <h4 className="font-medium text-zinc-900 mb-2">Key Scopes</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="bg-white rounded-lg p-3 border border-zinc-200">
                  <span className="font-medium text-zinc-900">read</span>
                  <p className="text-zinc-500 text-xs mt-1">Read-only access</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-zinc-200">
                  <span className="font-medium text-zinc-900">write</span>
                  <p className="text-zinc-500 text-xs mt-1">Create & update</p>
                </div>
                <div className="bg-white rounded-lg p-3 border border-zinc-200">
                  <span className="font-medium text-zinc-900">delete</span>
                  <p className="text-zinc-500 text-xs mt-1">Delete resources</p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'customers':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Customers API</h2>
              <p className="text-zinc-600 mt-2">Create, retrieve, update, and delete customers.</p>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">GET</span>
                  <code className="text-sm text-zinc-800">/v1/customers</code>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">List all customers with pagination.</p>
                <CodeBlock
                  code={`curl "https://${baseUrl}/api/v1/customers?limit=100&offset=0" \\
  -H "Authorization: Bearer pp_live_your_api_key"`}
                />
                <h4 className="font-medium text-zinc-900 mt-4 mb-2">Query Parameters</h4>
                <div className="text-sm space-y-2">
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">limit</code>
                    <span className="text-zinc-600">Number of results (1-1000, default 100)</span>
                  </div>
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">offset</code>
                    <span className="text-zinc-600">Skip N results for pagination</span>
                  </div>
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">zone</code>
                    <span className="text-zinc-600">Filter by zone/area</span>
                  </div>
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">search</code>
                    <span className="text-zinc-600">Search by name, email, or phone</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">POST</span>
                  <code className="text-sm text-zinc-800">/v1/customers</code>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">write</span>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">Create a new customer.</p>
                <CodeBlock
                  code={`curl -X POST "https://${baseUrl}/api/v1/customers" \\
  -H "Authorization: Bearer pp_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "John Smith",
    "email": "john@example.com",
    "phone": "07700 900000",
    "address": "123 Main Street, London",
    "zone": "Zone A"
  }'`}
                />
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">GET</span>
                  <code className="text-sm text-zinc-800">/v1/customers/:id</code>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">Retrieve a single customer by ID.</p>
                <CodeBlock
                  code={`curl "https://${baseUrl}/api/v1/customers/cust_123456" \\
  -H "Authorization: Bearer pp_live_your_api_key"`}
                />
              </div>
            </div>
          </div>
        );
        
      case 'jobs':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Jobs API</h2>
              <p className="text-zinc-600 mt-2">Manage scheduled jobs and appointments.</p>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">GET</span>
                  <code className="text-sm text-zinc-800">/v1/jobs</code>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">List jobs with filtering options.</p>
                <CodeBlock
                  code={`curl "https://${baseUrl}/api/v1/jobs?date_from=2026-03-01&date_to=2026-03-31" \\
  -H "Authorization: Bearer pp_live_your_api_key"`}
                />
                <h4 className="font-medium text-zinc-900 mt-4 mb-2">Query Parameters</h4>
                <div className="text-sm space-y-2">
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">customer_id</code>
                    <span className="text-zinc-600">Filter by customer</span>
                  </div>
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">status</code>
                    <span className="text-zinc-600">scheduled, completed, skipped</span>
                  </div>
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">date_from</code>
                    <span className="text-zinc-600">Filter from date (YYYY-MM-DD)</span>
                  </div>
                  <div className="flex gap-2">
                    <code className="bg-zinc-100 px-2 py-0.5 rounded">date_to</code>
                    <span className="text-zinc-600">Filter to date (YYYY-MM-DD)</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">POST</span>
                  <code className="text-sm text-zinc-800">/v1/jobs</code>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">write</span>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">Create a new job/appointment.</p>
                <CodeBlock
                  code={`curl -X POST "https://${baseUrl}/api/v1/jobs" \\
  -H "Authorization: Bearer pp_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_id": "cust_123456",
    "scheduled_date": "2026-03-15",
    "services": "Window Clean",
    "price": 35.00,
    "frequency": "every 6 weeks on Tuesday"
  }'`}
                />
              </div>
            </div>
          </div>
        );
        
      case 'invoices':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Invoices API</h2>
              <p className="text-zinc-600 mt-2">Retrieve and manage invoices.</p>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">GET</span>
                  <code className="text-sm text-zinc-800">/v1/invoices</code>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">List invoices with filtering.</p>
                <CodeBlock
                  code={`curl "https://${baseUrl}/api/v1/invoices?status=unpaid" \\
  -H "Authorization: Bearer pp_live_your_api_key"`}
                />
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">GET</span>
                  <code className="text-sm text-zinc-800">/v1/invoices/:id</code>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">Retrieve a single invoice.</p>
                <CodeBlock
                  code={`curl "https://${baseUrl}/api/v1/invoices/inv_123456" \\
  -H "Authorization: Bearer pp_live_your_api_key"`}
                />
              </div>
            </div>
          </div>
        );
        
      case 'webhooks':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Webhooks</h2>
              <p className="text-zinc-600 mt-2">Receive real-time events from external services.</p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Zap size={20} className="text-purple-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-purple-900">Available Webhooks</h4>
                  <p className="text-sm text-purple-700 mt-1">
                    Currently supports Facebook Lead Ads. More webhooks coming soon!
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl p-4">
              <h4 className="font-medium text-zinc-900 mb-2">Webhook Endpoints</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg">
                  <Facebook size={20} className="text-blue-600" />
                  <div>
                    <code className="text-sm text-zinc-800">/v1/webhooks/facebook-leads</code>
                    <p className="text-xs text-zinc-500">Auto-create customers from Facebook Lead Ads</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'facebook-leads':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Facebook Lead Ads Integration</h2>
              <p className="text-zinc-600 mt-2">
                Automatically create customers from Facebook Lead Ad submissions.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Facebook size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-blue-900">How it works</h3>
                  <p className="text-sm text-blue-700 mt-1">
                    When someone fills out your Facebook Lead Ad form, their details are automatically
                    sent to your StreakFree account and a new customer is created instantly.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl p-6 space-y-4">
              <h3 className="font-semibold text-zinc-900">Setup Instructions</h3>
              
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">1</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Create an API Key</h4>
                    <p className="text-sm text-zinc-600 mt-1">
                      Go to the API Keys section and create a key with "write" permissions.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">2</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Configure Facebook</h4>
                    <p className="text-sm text-zinc-600 mt-1">
                      In Facebook Business Manager, go to Events Manager → Your Pixel → Settings → Conversions API
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">3</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Add Webhook URL</h4>
                    <p className="text-sm text-zinc-600 mt-1">Use this URL for your webhook:</p>
                    <CodeBlock code={`https://${baseUrl}/api/v1/webhooks/facebook-leads`} />
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-700 font-semibold">4</span>
                  </div>
                  <div>
                    <h4 className="font-medium text-zinc-900">Add API Key Header</h4>
                    <p className="text-sm text-zinc-600 mt-1">
                      Add a custom header to the webhook:
                    </p>
                    <CodeBlock code={`X-API-Key: pp_live_your_api_key`} />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-emerald-900">Lead Fields Mapped</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    first_name, last_name, email, phone_number, street_address, city → Customer record
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'bulk-import':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-zinc-900">Bulk Import</h2>
              <p className="text-zinc-600 mt-2">
                Import customers in bulk from other systems like other CRM systems or spreadsheets.
              </p>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Upload size={20} className="text-emerald-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-emerald-900">Up to 500 customers per request</h4>
                  <p className="text-sm text-emerald-700 mt-1">
                    Perfect for migrating from other CRM systems.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl overflow-hidden">
              <div className="bg-zinc-50 px-4 py-3 border-b border-zinc-200">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">POST</span>
                  <code className="text-sm text-zinc-800">/v1/customers/bulk-import</code>
                  <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">write</span>
                </div>
              </div>
              <div className="p-4">
                <p className="text-zinc-600 text-sm mb-4">Import multiple customers at once.</p>
                <CodeBlock
                  code={`curl -X POST "https://${baseUrl}/api/v1/customers/bulk-import" \\
  -H "Authorization: Bearer pp_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '[
    {
      "name": "John Smith",
      "email": "john@example.com",
      "phone": "07700900001",
      "address": "123 Main St",
      "zone": "Zone A"
    },
    {
      "name": "Jane Doe",
      "email": "jane@example.com",
      "phone": "07700900002",
      "address": "456 Oak Ave",
      "zone": "Zone B"
    }
  ]'`}
                />
              </div>
            </div>
            
            <div className="border border-zinc-200 rounded-xl p-4">
              <h4 className="font-medium text-zinc-900 mb-3">Response</h4>
              <CodeBlock
                code={`{
  "status": "success",
  "created_count": 2,
  "error_count": 0,
  "errors": null
}`}
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <div className="h-full flex flex-col bg-white" data-testid="api-docs-page">
      {/* Header with safe area for iOS */}
      <div className="bg-zinc-900 text-white px-4 sm:px-6 py-4" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 16px)' }}>
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-bold truncate">API Documentation</h1>
            <p className="text-zinc-400 text-xs sm:text-sm hidden sm:block">Build integrations with the StreakFree API</p>
          </div>
          {/* Mobile menu button */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors lg:hidden"
          >
            <Book size={20} />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <div className={`
          fixed lg:relative inset-y-0 left-0 z-50 lg:z-auto
          w-64 border-r border-zinc-200 p-4 overflow-y-auto bg-zinc-50
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          lg:block
        `}>
          <div className="flex items-center justify-between mb-4 lg:hidden">
            <h2 className="font-semibold text-zinc-900">Sections</h2>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1 hover:bg-zinc-200 rounded"
            >
              <ChevronLeft size={20} />
            </button>
          </div>
          <DocsSidebar 
            activeSection={activeSection} 
            onNavigate={(section) => {
              setActiveSection(section);
              setSidebarOpen(false);
            }} 
          />
        </div>
        
        {/* Main content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl">
            {renderContent()}
          </div>
        </div>
      </div>
      
      {/* Create Key Modal */}
      {showCreateModal && (
        <CreateKeyModal
          onClose={() => setShowCreateModal(false)}
          onCreate={loadApiKeys}
        />
      )}
    </div>
  );
};

export default ApiDocs;
